import { StarRating } from "./star-rating";
import { formatDate } from "@/lib/utils";
import type { ProductReviewsData } from "@/types";

interface ReviewListProps {
  data: ProductReviewsData;
}

export function ReviewList({ data }: ReviewListProps) {
  const { summary, reviews } = data;

  if (summary.totalReviews === 0) {
    return (
      <div className="py-8 text-center text-gray-400">
        <p className="text-sm">Henüz yorum yok. İlk yorumu sen yaz!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Özet */}
      <div className="flex items-center gap-6 p-5 bg-gray-50 rounded-[var(--sf-radius)]">
        <div className="text-center">
          <p className="text-4xl font-bold text-gray-950">{summary.averageRating.toFixed(1)}</p>
          <StarRating rating={summary.averageRating} size={16} className="justify-center mt-1" />
          <p className="text-xs text-gray-500 mt-1">{summary.totalReviews} değerlendirme</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = summary.ratingBreakdown[star] ?? 0;
            const pct = summary.totalReviews > 0 ? (count / summary.totalReviews) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-4 shrink-0">{star}</span>
                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-4 shrink-0">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Yorum listesi */}
      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className="border-b border-gray-100 pb-4 last:border-0">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-900 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {r.reviewerName.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-semibold text-gray-800">{r.reviewerName}</span>
              </div>
              <span className="text-xs text-gray-400">{formatDate(r.createdAt)}</span>
            </div>
            <StarRating rating={r.rating} size={13} className="mb-2" />
            {r.title && <p className="text-sm font-semibold text-gray-900 mb-1">{r.title}</p>}
            {r.body && <p className="text-sm text-gray-600 leading-relaxed">{r.body}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
